import { useMemo, useEffect, useCallback, useRef, forwardRef } from 'react'
import { useScript } from '@bluheadless/ui-logic/src/hooks/use-script/useScript'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import router from 'next/router'
import { useNotification } from '@bluheadless/ui-logic/src/hooks/useNotification'
import { useIntl } from 'react-intl'
import { Root } from './paypal-express-checkout-button.styled'
import Progress from '../../atoms/progress/progress'
import { PAGE_PATH_CHECKOUT } from '@bluheadless/ui-logic/src/constants'
import { CHECKOUT_TYPE_PAYPAL_TWO_STEPS } from '@bluheadless/ui-logic/src/checkout/types'
import { keepOneSlash } from '@bluheadless/ui-logic/src/utils'
import { useStore } from '@bluheadless/ui-logic/src/providers/store'
import { unstable_useForkRef as useForkRef } from '@mui/utils'
import { useApiAction } from '@bluheadless/ui-logic/src/hooks/action'
import { postSalesPaymentInit } from 'data-api'

const ERROR_POPUP_CLOSE = 'Detected popup close'
const ERROR_COMPONENT_CLOSED = 'Component closed'
const ERROR_COMPONENT_DESTROYED = 'Component destroyed'

const PayPalExpressCheckoutButton = forwardRef(function PayPalExpressCheckoutButton(
	{ options, forceReloadOnApprove = false, returnStep, ...props },
	ref
) {
	const {
		sales: {
			payments: {
				paypal: {
					expressCheckout: { enabled },
					clientId,
					scriptUrl,
					color = 'gold',
					layout = 'horizontal',
					tagline = false,
				},
			},
		},
		defaultCurrency,
		locale,
	} = useConfig()

	const { action: initPayment } = useApiAction({ method: postSalesPaymentInit })
	const { baseUrl, path } = useStore()

	const { enqueueNotification } = useNotification()

	const buttonRef = useRef(null)
	const handleButtonRef = useForkRef(ref, buttonRef)

	const localeCode = locale.replace('-', '_')

	const src = useMemo(() => {
		const srcUrl = new URL(scriptUrl)
		srcUrl.search = new URLSearchParams({
			'client-id': clientId,
			components: 'buttons',
			intent: 'authorize',
			commit: false,
			currency: defaultCurrency,
			locale: localeCode,
		}).toString()

		return srcUrl
	}, [clientId, defaultCurrency, scriptUrl, localeCode])

	const scriptStatus = useScript({ src: enabled && src })
	const paypalRef = useRef(null)
	const { formatMessage } = useIntl()

	const createOrder = useCallback(
		// eslint-disable-next-line no-unused-vars
		(data, actions) =>
			initPayment({ code: 'filoblu_paypal_express_checkout' })
				.then((response) => response)
				.then((order) => order?.id ?? Promise.reject(new Error('no order id returned.')))
				.catch((error) => {
					throw new Error(`createOrder failed: ${error?.message}`)
				}),
		[initPayment]
	)

	const onApprove = useCallback(
		(data) => {
			const returnPath = keepOneSlash(`${path}${PAGE_PATH_CHECKOUT}`)
			const returnUrl = new URL(returnPath, baseUrl)
			if (returnStep) {
				returnUrl.hash = returnStep
			}
			returnUrl.search = new URLSearchParams({
				type: CHECKOUT_TYPE_PAYPAL_TWO_STEPS,
				paypalExpressCheckoutOrderId: data.orderID,
			}).toString()
			if (forceReloadOnApprove) {
				window.location = returnUrl
			} else {
				router.push(returnUrl)
			}
		},
		[baseUrl, path, forceReloadOnApprove, returnStep]
	)

	const onError = useCallback(
		(error) => {
			if (error.message === ERROR_POPUP_CLOSE) {
				// ignore popup close error
				return
			}
			console.error(error)
			enqueueNotification({ uiProps: { severity: 'error', message: formatMessage({ id: 'an_error_occurred' }) } })
		},
		[enqueueNotification, formatMessage]
	)

	const closeButtonsComponent = useCallback(() => {
		if (paypalRef.current !== null) {
			paypalRef.current.close().catch(() => {
				// ignore errors when closing the component
			})
		}
	}, [])

	useEffect(() => {
		if (scriptStatus === 'ready') {
			/** @see {@link https://developer.paypal.com/sdk/js/reference/#buttons} */
			paypalRef.current = window.paypal.Buttons({
				createOrder,
				onApprove,
				onError,
				...options,
				/** @see {@link https://developer.paypal.com/sdk/js/reference/#style} */
				style: {
					color, // gold | blue | silver | black
					layout, // vertical | horizontal
					tagline, // true | false
					label: 'checkout',
					...options?.style,
				},
			})

			if (!paypalRef.current.isEligible()) {
				return closeButtonsComponent
			}

			// Render the PayPal button in the DOM.
			paypalRef.current.render(buttonRef.current).catch((err) => {
				// component failed to render, possibly because it was closed or destroyed.
				if (
					buttonRef.current === null ||
					buttonRef.current.children.length === 0 ||
					err.message === ERROR_COMPONENT_CLOSED ||
					err.message === ERROR_COMPONENT_DESTROYED
				) {
					// paypal buttons container is no longer in the DOM, we can safely ignore the error
					return
				}
				console.error(err)
				enqueueNotification({ uiProps: { severity: 'error', message: formatMessage({ id: 'an_error_occurred' }) } })
			})

			// Close the PayPal instance otherwise multiple instances will be created.
			return closeButtonsComponent
		}
	}, [
		scriptStatus,
		color,
		layout,
		tagline,
		createOrder,
		onApprove,
		onError,
		enqueueNotification,
		formatMessage,
		options,
		closeButtonsComponent,
	])

	const loading = scriptStatus !== 'ready'

	return (
		enabled && (
			<>
				<Root ref={handleButtonRef} {...props}>
					{loading && <Progress />}
				</Root>
			</>
		)
	)
})

export default PayPalExpressCheckoutButton
